import { useRoute } from '#imports'

import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import { getLiveShoppingPageContext } from './useIsLiveShoppingEnabled'
import type { LiveShoppingProduct } from './useLiveShoppingProducts'

// Page-specific `zone` values, per Anto's tracking plan in the Live shopping
// POC PRD. The plan specifies these as the sole differentiator for identifying
// both the page and the live shopping overlay.
const ZONE_BY_BUCKET = {
  home: 'home_live_shopping_widget',
  plpSearch: 'plp_live_shopping_widget',
  pdp: 'pdp_live_shopping_widget',
} as const

export function useLiveShoppingTracking() {
  const route = useRoute()
  const {
    trackClick,
    trackProductClick,
    trackAddToCart: trackAddToCartEvent,
    trackLiveShoppingOverlayClosed,
    trackLiveShoppingWidgetDismissed,
    trackLiveShoppingVideoStarted,
    trackLiveShoppingVideoPlayed,
    trackLiveShoppingJumpToMoment,
  } = useTracking()

  // Resolves the page-specific zone from the current route at event-fire
  // time (not at mount time), so SPA navigation between buckets (home → PDP)
  // correctly attributes the event to the page the user is on when they
  // interact, not where the widget was first mounted.
  function zone() {
    const bucket = getLiveShoppingPageContext(
      route.name,
      route.path,
      route.query,
    )?.bucket

    return bucket ? ZONE_BY_BUCKET[bucket] : 'live_shopping_widget'
  }

  // Uses the existing `clicked` event (per Anto's plan) with `zone`.
  function trackOverlayOpened() {
    trackClick({ zone: zone() })
  }

  // Custom event — "Are visitors dismissing the modal?"
  function trackOverlayClosed() {
    trackLiveShoppingOverlayClosed({ zone: zone() })
  }

  // Custom event — "Are visitors dismissing the widget?" (the collapsed
  // pill's X button, without ever opening the modal).
  function trackOverlayDismissed() {
    trackLiveShoppingWidgetDismissed({ zone: zone() })
  }

  // Custom event — video started.
  function trackVideoStarted() {
    trackLiveShoppingVideoStarted({ zone: zone() })
  }

  // Uses the existing `product_clicked` event (per Anto's plan) with `zone`.
  function trackProductClicked(product: LiveShoppingProduct, position: number) {
    trackProductClick({
      zone: zone(),
      objectId: product.objectId,
      listingId: product.listingId,
      title: product.title,
      position,
    })
  }

  // Custom event — "jump_to_moment".
  function trackJumpToMomentClicked(
    product: LiveShoppingProduct,
    position: number,
  ) {
    trackLiveShoppingJumpToMoment({
      zone: zone(),
      objectId: product.objectId,
      timestampSeconds: product.timestampSeconds,
      position,
    })
  }

  // Uses the existing `add_to_cart` event (per Anto's plan) with `zone`.
  function trackAddToCart(product: LiveShoppingProduct, position: number) {
    trackAddToCartEvent({
      zone: zone(),
      objectId: product.objectId,
      listingId: product.listingId,
      title: product.title,
      position,
    })
  }

  // Custom event — "video_played" with `percent` (25/50/75/100).
  function trackVideoProgress(percent: number) {
    trackLiveShoppingVideoPlayed({ zone: zone(), percent })
  }

  return {
    trackOverlayOpened,
    trackOverlayClosed,
    trackOverlayDismissed,
    trackVideoStarted,
    trackVideoProgress,
    trackProductClicked,
    trackJumpToMomentClicked,
    trackAddToCart,
  }
}
